import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Store,
  Gift,
  BarChart3,
  Monitor,
  ListChecks,
  Megaphone,
  CreditCard,
  TrendingUp,
  Settings,
  LogOut,
  Search,
  RefreshCw,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Receipt,
  Timer,
  DollarSign,
  Activity,
  Zap,
  Filter,
  Calendar,
  Wallet,
  Package,
  Shield,
  Camera,
  Video,
  ClipboardList,
  Building,
  MapPin,
  History,
  ArrowUpRight,
  ArrowDownRight,
  Ban,
  CircleDot,
  Info
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

export default function AdminVehicleHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchedVrm, setSearchedVrm] = useState("");
  const [historyData, setHistoryData] = useState(null);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  
  // Consume site from Dashboard's localStorage selection
  const [effectiveSiteId, setEffectiveSiteId] = useState(null);
  const [siteName, setSiteName] = useState("");

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (err) {
        navigate("/admin/login");
      }
    };
    checkAuth();
  }, [navigate]);

  // Determine effective site from Dashboard selection or single authorised site
  useEffect(() => {
    const determineEffectiveSite = async () => {
      if (!user) return;

      const userSiteIds = user.site_ids || [];
      
      // Single authorised site: use it automatically
      if (userSiteIds.length === 1) {
        setEffectiveSiteId(userSiteIds[0]);
        // Fetch site name
        try {
          const response = await axios.get(`${API}/sites`, { withCredentials: true });
          const site = (response.data || []).find(s => s.site_id === userSiteIds[0]);
          setSiteName(site ? site.name : userSiteIds[0]);
        } catch (err) {
          setSiteName(userSiteIds[0]);
        }
        return;
      }

      // Multiple sites: use Dashboard's selection from localStorage
      const dashboardSite = localStorage.getItem('adminSelectedSite');
      
      if (dashboardSite && dashboardSite !== "all" && userSiteIds.includes(dashboardSite)) {
        setEffectiveSiteId(dashboardSite);
        // Fetch site name
        try {
          const response = await axios.get(`${API}/sites`, { withCredentials: true });
          const site = (response.data || []).find(s => s.site_id === dashboardSite);
          setSiteName(site ? site.name : dashboardSite);
        } catch (err) {
          setSiteName(dashboardSite);
        }
      } else {
        // No specific site selected or "all" selected
        setEffectiveSiteId(null);
        setSiteName("");
      }
    };
    
    determineEffectiveSite();
    
    // Listen for storage changes (if Dashboard changes site in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'adminSelectedSite') {
        // Clear results when site changes
        setHistoryData(null);
        setError(null);
        setSearchedVrm("");
        determineEffectiveSite();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  // Get display text for current site status
  const getSiteStatusText = () => {
    if (!user) return "Loading...";
    const userSiteIds = user.site_ids || [];
    if (userSiteIds.length === 0) return "No authorised sites";
    if (userSiteIds.length === 1) return siteName || "Loading site...";
    if (effectiveSiteId) return siteName || effectiveSiteId;
    return "Select a site from the Dashboard";
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('session_token');
    navigate("/admin/login");
  };

  const handleSearch = async () => {
    const vrm = searchInput.trim().toUpperCase().replace(/\s+/g, '');
    
    if (!vrm) {
      toast.error("Please enter a vehicle registration");
      return;
    }

    // Require specific site (not "all")
    if (!effectiveSiteId) {
      toast.error("Please select a specific site from the Dashboard first");
      return;
    }

    setLoading(true);
    setError(null);
    setHistoryData(null);
    setSearchedVrm(vrm);

    try {
      // Include effective site_id in API request
      const response = await axios.get(
        `${API}/parking/sessions/history/${encodeURIComponent(vrm)}?limit=100&site_id=${encodeURIComponent(effectiveSiteId)}`,
        { withCredentials: true }
      );
      setHistoryData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("No recorded vehicle history found.");
      } else if (err.response?.status === 403) {
        setError("Not authorised for this site.");
      } else if (err.response?.status === 422) {
        setError("Please select a site to search.");
      } else {
        setError("Unable to retrieve vehicle history. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Format helpers
  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  // Get friendly label for event type/action
  const getEventLabel = (event) => {
    const action = event.action;
    const reason = event.reason;
    const type = event.type;
    const barrier = event.barrier;

    // PAID_EXIT -> Paid — Exit Authorised
    if (action === "PAID_EXIT" || (action === "FREE_EXIT" && reason === "paid")) {
      return { label: "Paid — Exit Authorised", color: "text-green-400", bg: "bg-green-500/10", icon: CheckCircle };
    }

    // CHARGE + CLOSED -> Payment Required — Barrier Closed
    if (action === "CHARGE" && barrier === "CLOSED") {
      return { label: "Payment Required — Barrier Closed", color: "text-yellow-400", bg: "bg-yellow-500/10", icon: Ban };
    }

    // FREE_EXIT with whitelist reason
    if (action === "FREE_EXIT" && reason && reason.toLowerCase().includes("whitelist")) {
      return { label: "Whitelist — Free Exit", color: "text-purple-400", bg: "bg-purple-500/10", icon: Shield };
    }

    // FREE_EXIT with free parking period reason
    if (action === "FREE_EXIT" && reason && (reason.toLowerCase().includes("free parking") || reason.toLowerCase().includes("grace"))) {
      return { label: "Free Parking Period — Exit Authorised", color: "text-green-400", bg: "bg-green-500/10", icon: CheckCircle };
    }

    // NO_SESSION_FREE_EXIT
    if (action === "NO_SESSION_FREE_EXIT") {
      return { label: "No Session — Free Exit", color: "text-blue-400", bg: "bg-blue-500/10", icon: ArrowUpRight };
    }

    // other FREE_EXIT
    if (action === "FREE_EXIT") {
      return { label: "Free Exit", color: "text-green-400", bg: "bg-green-500/10", icon: ArrowUpRight };
    }

    // BARRIER lift successful/open
    if (type === "barrier" || action === "BARRIER") {
      if (barrier === "OPEN" || event.status === "success" || event.status === "open") {
        return { label: "Barrier Opened", color: "text-green-400", bg: "bg-green-500/10", icon: CheckCircle };
      }
      return { label: "Barrier Event", color: "text-slate-400", bg: "bg-slate-500/10", icon: Activity };
    }

    // PAYMENT
    if (type === "payment" || action === "PAYMENT") {
      return { label: "Payment", color: "text-green-400", bg: "bg-green-500/10", icon: CreditCard };
    }

    // ENTRY
    if (type === "entry" || action === "ENTRY") {
      return { label: "Entry", color: "text-blue-400", bg: "bg-blue-500/10", icon: ArrowDownRight };
    }

    // EXIT
    if (type === "exit" || action === "EXIT") {
      return { label: "Exit", color: "text-purple-400", bg: "bg-purple-500/10", icon: ArrowUpRight };
    }

    // NO_SESSION_BLOCKED - display as historical evidence
    if (action === "NO_SESSION_BLOCKED") {
      return { label: "No Session — Blocked", color: "text-red-400", bg: "bg-red-500/10", icon: Ban };
    }

    // Default fallback
    return { label: action || type || "Event", color: "text-slate-400", bg: "bg-slate-500/10", icon: CircleDot };
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-vehicle-history">
      {/* Sidebar - matching AdminLiveSessions */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-1">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
            <LayoutDashboard className="w-4 h-4" />
            <span>Dashboard</span>
          </Link>
          
          {/* Operations */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Operations</p>
            <Link to="/admin/freeflow" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Shield className="w-4 h-4" />
              <span>Freeflow Whitelist</span>
            </Link>
            <Link to="/admin/exit-auths" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Clock className="w-4 h-4" />
              <span>Exit Authorisations</span>
            </Link>
            <Link to="/admin/barrier" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Activity className="w-4 h-4" />
              <span>Barrier Control</span>
            </Link>
            <Link to="/admin/cameras" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Camera className="w-4 h-4" />
              <span>Cameras</span>
            </Link>
            <Link to="/admin/cctv" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Video className="w-4 h-4" />
              <span>CCTV</span>
            </Link>
            <Link to="/admin/daily-movement" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <ClipboardList className="w-4 h-4" />
              <span>Daily VRM Movement</span>
            </Link>
            <Link to="/admin/vehicle-history" className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-slate-800 text-white transition-colors text-sm">
              <History className="w-4 h-4" />
              <span>Vehicle History</span>
            </Link>
          </div>
          
          {/* Commercial */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <DollarSign className="w-4 h-4" />
              <span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Package className="w-4 h-4" />
              <span>Products & Tariffs</span>
            </Link>
            <Link to="/admin/commercial/sites" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Building className="w-4 h-4" />
              <span>Sites</span>
            </Link>
          </div>

          {/* Admin */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Admin</p>
            <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <FileText className="w-4 h-4" />
              <span>Registrations</span>
            </Link>
            <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Building className="w-4 h-4" />
              <span>Sites & Kiosks</span>
            </Link>
            <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>OCR Exceptions</span>
            </Link>
          </div>

          {/* Engagement */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Engagement</p>
            <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Store className="w-4 h-4" />
              <span>Retailers</span>
            </Link>
            <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Gift className="w-4 h-4" />
              <span>Rewards</span>
            </Link>
            <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </Link>
          </div>

          {/* Signage */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Signage</p>
            <Link to="/admin/screens" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Monitor className="w-4 h-4" />
              <span>Screens</span>
            </Link>
            <Link to="/admin/playlists" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <ListChecks className="w-4 h-4" />
              <span>Playlists</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <FileText className="w-4 h-4" />
              <span>Content</span>
            </Link>
          </div>

          {/* Revenue */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Revenue</p>
            <Link to="/admin/advertising" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors text-sm">
              <Megaphone className="w-4 h-4" />
              <span>Advertising</span>
            </Link>
          </div>
        </nav>

        {/* User */}
        <div className="absolute bottom-6 left-6 right-6">
          {user && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-800 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                {user.name?.charAt(0) || user.email?.charAt(0) || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user.name || "Admin"}</p>
                <p className="text-xs text-slate-400 truncate">{user.email}</p>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Vehicle History</h1>
          <p className="text-slate-400">Search a vehicle registration to view its recorded parking history and barrier decisions.</p>
        </div>

        {/* Current Site Status - uses Dashboard's selection */}
        <Card className="bg-slate-900 border-slate-800 mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-medium">Site:</span>
              </div>
              <div className="text-white font-medium">
                {getSiteStatusText()}
              </div>
              {!effectiveSiteId && user?.site_ids?.length > 1 && (
                <Link 
                  to="/admin/dashboard" 
                  className="text-xs text-blue-400 hover:text-blue-300 underline"
                >
                  Go to Dashboard to select a site
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Enter vehicle registration (e.g. AB12CDE)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  onKeyPress={handleKeyPress}
                  maxLength={9}
                  className="bg-slate-800 border-slate-700 text-white text-lg font-mono tracking-wider"
                  data-testid="vrm-search-input"
                  disabled={!effectiveSiteId}
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={loading || !searchInput.trim() || !effectiveSiteId}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                data-testid="search-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>
            {!effectiveSiteId && user?.site_ids?.length > 1 && (
              <p className="text-sm text-amber-400 mt-2">Select a specific site from the Dashboard to search vehicle history.</p>
            )}
            {!effectiveSiteId && user?.site_ids?.length === 0 && (
              <p className="text-sm text-red-400 mt-2">No authorised sites. Contact administrator.</p>
            )}
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="bg-slate-900 border-slate-800 mb-8">
            <CardContent className="p-8 text-center">
              <Info className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {historyData && (
          <>
            {/* Summary Header */}
            <Card className="bg-slate-900 border-slate-800 mb-6">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Searched VRM</p>
                    <p className="text-xl font-mono font-bold text-white">{searchedVrm}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Matched VRMs</p>
                    <p className="text-lg text-white">
                      {historyData.matched_vehicle_registrations?.join(", ") || searchedVrm}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Sessions</p>
                    <p className="text-xl font-bold text-white">{historyData.sessions?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Events</p>
                    <p className="text-xl font-bold text-white">{historyData.events?.length || 0}</p>
                  </div>
                </div>

                {/* Fuzzy Match Notice */}
                {historyData.historical_match_type === "fuzzy" && (
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-amber-400 font-medium">Historical fuzzy match</p>
                        <p className="text-slate-300 text-sm mt-1">
                          The searched registration was uniquely matched to historical records for: {historyData.matched_vehicle_registrations?.join(", ")}
                        </p>
                        {historyData.historical_match_distance !== undefined && (
                          <p className="text-slate-400 text-sm mt-1">
                            Match distance: {historyData.historical_match_distance}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Ambiguous Match Notice */}
                {historyData.historical_match_ambiguous && (
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-amber-400 font-medium">Ambiguous match</p>
                        <p className="text-slate-300 text-sm mt-1">
                          Multiple possible historical matches were found. No ambiguous vehicle history has been merged.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Events Timeline */}
            {historyData.events && historyData.events.length > 0 && (
              <Card className="bg-slate-900 border-slate-800 mb-6">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Event Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-800">
                    {historyData.events
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((event, index) => {
                        const eventInfo = getEventLabel(event);
                        const EventIcon = eventInfo.icon;
                        
                        return (
                          <div key={index} className="p-4 hover:bg-slate-800/50 transition-colors">
                            <div className="flex items-start gap-4">
                              {/* Event Icon */}
                              <div className={`w-10 h-10 rounded-lg ${eventInfo.bg} flex items-center justify-center flex-shrink-0`}>
                                <EventIcon className={`w-5 h-5 ${eventInfo.color}`} />
                              </div>
                              
                              {/* Event Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-1">
                                  {/* Direction badge - only show if event.direction exists */}
                                  {event.direction && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider ${
                                      event.direction === "entry" 
                                        ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" 
                                        : "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                                    }`}>
                                      {event.direction === "entry" ? "ENTRY" : "EXIT"}
                                    </span>
                                  )}
                                  {event.direction && (
                                    <span className="text-slate-600">·</span>
                                  )}
                                  <span className={`font-semibold ${eventInfo.color}`}>
                                    {eventInfo.label}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {formatDateTime(event.timestamp)}
                                  </span>
                                </div>
                                
                                {/* Event metadata */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                  {event.vehicle_registration && (
                                    <div>
                                      <span className="text-slate-500">VRM: </span>
                                      <span className="text-white font-mono">{event.vehicle_registration}</span>
                                    </div>
                                  )}
                                  {event.amount !== undefined && event.amount !== null && (
                                    <div>
                                      <span className="text-slate-500">Amount: </span>
                                      <span className="text-green-400">{formatCurrency(event.amount)}</span>
                                    </div>
                                  )}
                                  {event.barrier && (
                                    <div>
                                      <span className="text-slate-500">Barrier: </span>
                                      <span className={event.barrier === "OPEN" ? "text-green-400" : "text-red-400"}>
                                        {event.barrier}
                                      </span>
                                    </div>
                                  )}
                                  {event.status && (
                                    <div>
                                      <span className="text-slate-500">Status: </span>
                                      <span className="text-white">{event.status}</span>
                                    </div>
                                  )}
                                  {event.site_id && (
                                    <div>
                                      <span className="text-slate-500">Site: </span>
                                      <span className="text-slate-300">{event.site_id}</span>
                                    </div>
                                  )}
                                  {event.session_id && (
                                    <div>
                                      <span className="text-slate-500">Session: </span>
                                      <span className="text-slate-300 font-mono text-xs">{event.session_id}</span>
                                    </div>
                                  )}
                                  {event.camera_id && (
                                    <div>
                                      <span className="text-slate-500">Camera: </span>
                                      <span className="text-slate-300">{event.camera_id}</span>
                                    </div>
                                  )}
                                  {event.barrier_id && (
                                    <div>
                                      <span className="text-slate-500">Barrier ID: </span>
                                      <span className="text-slate-300">{event.barrier_id}</span>
                                    </div>
                                  )}
                                  {event.reference && (
                                    <div>
                                      <span className="text-slate-500">Ref: </span>
                                      <span className="text-slate-300 font-mono text-xs">{event.reference}</span>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Reason if present */}
                                {event.reason && (
                                  <p className="text-slate-400 text-sm mt-1">
                                    Reason: {event.reason}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sessions Section */}
            {historyData.sessions && historyData.sessions.length > 0 && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Car className="w-5 h-5" />
                    Parking Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-800">
                          <th className="text-left text-xs text-slate-500 uppercase tracking-wider p-4">Session ID</th>
                          <th className="text-left text-xs text-slate-500 uppercase tracking-wider p-4">VRM</th>
                          <th className="text-left text-xs text-slate-500 uppercase tracking-wider p-4">Entry</th>
                          <th className="text-left text-xs text-slate-500 uppercase tracking-wider p-4">Exit</th>
                          <th className="text-left text-xs text-slate-500 uppercase tracking-wider p-4">Status</th>
                          <th className="text-right text-xs text-slate-500 uppercase tracking-wider p-4">Fee</th>
                          <th className="text-right text-xs text-slate-500 uppercase tracking-wider p-4">Paid</th>
                          <th className="text-left text-xs text-slate-500 uppercase tracking-wider p-4">Site</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.sessions.map((session, index) => (
                          <tr key={index} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                            <td className="p-4">
                              <span className="text-slate-300 font-mono text-xs">{session.session_id}</span>
                            </td>
                            <td className="p-4">
                              <span className="text-white font-mono">{session.vehicle_registration}</span>
                            </td>
                            <td className="p-4">
                              <span className="text-slate-300 text-sm">{formatDateTime(session.entry_time)}</span>
                            </td>
                            <td className="p-4">
                              <span className="text-slate-300 text-sm">
                                {session.exit_time ? formatDateTime(session.exit_time) : "—"}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                session.status === "completed" ? "bg-green-500/20 text-green-400" :
                                session.status === "active" ? "bg-blue-500/20 text-blue-400" :
                                "bg-slate-500/20 text-slate-400"
                              }`}>
                                {session.status}
                              </span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="text-white">{formatCurrency(session.fee_calculated)}</span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="text-green-400">{formatCurrency(session.amount_paid)}</span>
                            </td>
                            <td className="p-4">
                              <span className="text-slate-400 text-sm">{session.site_id}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Empty state before search */}
        {!loading && !error && !historyData && (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-12 text-center">
              <History className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Enter a vehicle registration to view its parking history</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
